/**
 * Builds and posts auth-letter review status rows to CPT tables for the parent
 * order and filtered sibling orders.
 *
 * @module syncAuthLetterReviewStatusToCptTables
 */

import {
  updateCPTtables,
  UpdateCPTtablesPayload,
} from "../../../../api/postCall/updateCPTtables";
import { mapSiblingJCodeIdsByOrderId } from "../../../../api/postgreSQL/utils/buildAllJCodeDocumentUpdatePayloads";
import {
  AuthOnFile,
  AuthRangeResponse,
} from "../../../../data-model/medicalPaOrdersModel";
import { SiblingOrdersResponse } from "../../../../data-model/siblingOrdersModel";
import { AuthLetterReviewStatus } from "../../../../enums/authLetterReviewStatus";
import { getAllJCodesFromAuthOnFile } from "../../../../pages/singleOrderLayout/utils/existingDocsToLs";
import {
  getAuthLetterReviewStatusPerJCode,
  setAuthLetterReviewStatusOnJCodes,
} from "../../../../utils/updateJcodesWithCorrectDocRef";

/** Minimal J-code row accepted by the CPT tables update endpoint. */
export interface AuthLetterReviewStatusRow {
  /** J-code row id on the target order. */
  id: string;
  /** Auth-letter review status to persist on the CPT row. */
  auth_letter_review_status: string;
}

/**
 * Resolves parent `j_code` values aligned to each review status row.
 *
 * @param statuses - Parent-order review status rows
 * @param parentAuthOnFile - Parent auth-on-file snapshot
 * @returns Parent J-code strings in the same order as {@link statuses}
 */
export const getParentJCodeValuesForReviewStatuses = (
  statuses: AuthLetterReviewStatusRow[],
  parentAuthOnFile: AuthOnFile,
): string[] => {
  const parentJCodes = getAllJCodesFromAuthOnFile(parentAuthOnFile) ?? [];

  return statuses.map(
    (row) =>
      parentJCodes.find(
        (jCode) => jCode.id === row.id || jCode.j_code === row.id,
      )?.j_code ?? "",
  );
};

/**
 * Maps sibling J-code row ids to review status rows using the parent row order.
 *
 * @param parentStatuses - Parent-order review status rows
 * @param siblingJCodeIds - Sibling J-code row ids aligned to {@link parentStatuses}
 * @returns Sibling-order review status rows with remapped ids
 */
export const buildSiblingAuthLetterReviewStatusRows = (
  parentStatuses: AuthLetterReviewStatusRow[],
  siblingJCodeIds: string[],
): AuthLetterReviewStatusRow[] => {
  return siblingJCodeIds.flatMap((siblingJCodeId, index) => {
    if (!siblingJCodeId) {
      return [];
    }

    return [
      {
        id: siblingJCodeId,
        auth_letter_review_status:
          parentStatuses[index]?.auth_letter_review_status ?? "",
      },
    ];
  });
};

/**
 * Builds CPT update payloads for the parent order and each filtered sibling.
 *
 * @param parentOrderId - Order receiving the auth-letter upload
 * @param orgId - Organisation id shared across payloads
 * @param parentStatuses - Review status rows for the parent order
 * @param filteredSiblingOrders - Filtered sibling orders from the data accumulator
 * @param parentAuthOnFile - Parent auth-on-file snapshot used for J-code remapping
 * @returns One payload per order that has at least one review status row
 */
export const buildAuthLetterReviewCptUpdatePayloads = (
  parentOrderId: string,
  orgId: string,
  parentStatuses: AuthLetterReviewStatusRow[],
  filteredSiblingOrders: SiblingOrdersResponse | null | undefined,
  parentAuthOnFile: AuthOnFile,
): UpdateCPTtablesPayload[] => {
  const payloads: UpdateCPTtablesPayload[] = [
    {
      order_id: parentOrderId,
      org_id: orgId,
      j_codes: parentStatuses,
    },
  ];

  const parentJCodeValues = getParentJCodeValuesForReviewStatuses(
    parentStatuses,
    parentAuthOnFile,
  );
  const siblingJCodeIdsByOrderId = mapSiblingJCodeIdsByOrderId(
    parentJCodeValues,
    filteredSiblingOrders?.siblings ?? [],
  );

  for (const [siblingOrderId, siblingJCodeIds] of Object.entries(
    siblingJCodeIdsByOrderId,
  )) {
    const siblingStatuses = buildSiblingAuthLetterReviewStatusRows(
      parentStatuses,
      siblingJCodeIds,
    );

    if (siblingStatuses.length === 0) {
      continue;
    }

    payloads.push({
      order_id: siblingOrderId,
      org_id: orgId,
      j_codes: siblingStatuses,
    });
  }

  return payloads;
};

/**
 * Posts auth-letter review status rows to CPT tables for parent and siblings.
 *
 * @param parentOrderId - Order receiving the auth-letter upload
 * @param orgId - Organisation id shared across payloads
 * @param parentStatuses - Review status rows for the parent order
 * @param filteredSiblingOrders - Filtered sibling orders from the data accumulator
 * @param parentAuthOnFile - Parent auth-on-file snapshot used for J-code remapping
 * @param signal - Optional abort signal shared across requests
 * @returns Resolves when every CPT update completes
 */
export const syncAuthLetterReviewStatusToCptTables = async (
  parentOrderId: string,
  orgId: string,
  parentStatuses: AuthLetterReviewStatusRow[],
  filteredSiblingOrders: SiblingOrdersResponse | null | undefined,
  parentAuthOnFile: AuthOnFile,
  signal?: AbortSignal,
): Promise<void> => {
  const payloads = buildAuthLetterReviewCptUpdatePayloads(
    parentOrderId,
    orgId,
    parentStatuses,
    filteredSiblingOrders,
    parentAuthOnFile,
  );

  await Promise.all(
    payloads.map((payload) => updateCPTtables(payload, signal)),
  );
};

/**
 * Marks selected J-codes as "new letter uploaded" and syncs CPT tables.
 *
 * @param entries - Auth entries before the review-status mutation
 * @param selectedMedicines - J-code ids selected in the NAR letter modal
 * @param parentOrderId - Order receiving the auth-letter upload
 * @param orgId - Organisation id shared across payloads
 * @param filteredSiblingOrders - Filtered sibling orders from the data accumulator
 * @param signal - Optional abort signal shared across requests
 * @returns Auth entries with the updated review status applied
 */
export const applyAuthLetterUploadedReviewStatus = async (
  entries: AuthRangeResponse[],
  selectedMedicines: string[],
  parentOrderId: string,
  orgId: string,
  filteredSiblingOrders: SiblingOrdersResponse | null | undefined,
  signal?: AbortSignal,
): Promise<AuthRangeResponse[]> => {
  const updatedEntries = setAuthLetterReviewStatusOnJCodes(
    entries,
    selectedMedicines,
    AuthLetterReviewStatus.NewLetterUploaded,
  );
  const parentAuthOnFile: AuthOnFile = { entries: updatedEntries };
  const parentStatuses = getAuthLetterReviewStatusPerJCode(updatedEntries);

  await syncAuthLetterReviewStatusToCptTables(
    parentOrderId,
    orgId,
    parentStatuses,
    filteredSiblingOrders,
    parentAuthOnFile,
    signal,
  );

  return updatedEntries;
};
